/**
 * A ponte entre o motor do LiveKit (media) e a nossa API (autoridade).
 *
 * Regra do desenho: o LiveKit sabe quem está a falar; a NOSSA API é que sabe
 * quem tem direito a falar. Por isso cada acção local é espelhada em
 * `/voice/state` — é isso que mantém o ícone de mute correcto para toda a
 * gente, incluindo para quem entrou depois e nunca viu o evento.
 */

import { useCallback, useSyncExternalStore } from "react";

import { api, type VoiceJoinResult } from "../lib/api";
import { resolveWs } from "../lib/host";
import type { NetSample } from "../lib/netStats";
import { playSound, primeSounds } from "../lib/sounds";
import { type ConnectOptions, type VoiceConnState, type VoiceSnapshot, voiceEngine } from "../lib/voice";
import type { EngineVoice } from "../store/selectors";
import { useStore } from "../store/store";

/**
 * As opções de ligação ao motor — e a regra do microfone, escrita UMA vez.
 *
 * Entra-se MUDO em qualquer chamada: canal de voz, call de sala, TV, e sobretudo
 * o huddle de corredor, onde a entrada não é um clique mas um PASSO — basta
 * parar ao lado de alguém. Um microfone que abre sozinho por proximidade põe
 * metade do escritório a ouvir uma conversa que ninguém decidiu ter. Quem quer
 * falar carrega no botão; é um gesto, não um risco.
 *
 * Repare-se que NÃO é `canSpeak` que decide (era o que estava aqui): a permissão
 * responde a "pode falar?", não a "quer falar já". Ligar mudo tem ainda o efeito
 * de não pedir o microfone ao browser à entrada — a autorização só é pedida a
 * quem a vai mesmo usar.
 *
 * Vive aqui, e não copiada em cada sítio, porque os caminhos de entrada são dois
 * e moram em ficheiros diferentes: este (`join`, que serve canal/sala/TV/lobby) e
 * o huddle em `OfficeSpace`. Duas cópias da mesma regra é uma regra que muda só
 * de um lado.
 */
export function connectMuted(result: VoiceJoinResult): ConnectOptions {
  return {
    // O `LIVEKIT_URL` do backend também vem `localhost`: sem o resolver, o
    // browser de um colega tentava falar com o SFU da máquina dele e nunca
    // chegava som/vídeo a ninguém — o motivo #1 de "a transmissão não vai".
    // Na ponte vem `/livekit` e monta-se sobre a origem da página (wss://).
    url: resolveWs(result.url),
    token: result.token,
    micEnabled: false,
    deafened: false,
  };
}

const EMPTY: VoiceSnapshot = {
  state: "idle",
  error: null,
  participants: [],
  micEnabled: false,
  cameraEnabled: false,
  screenEnabled: false,
  screenAudioAvailable: false,
  screenAudioMuted: false,
  deafened: false,
  needsAudioUnlock: false,
  raisedHands: [],
  privateTo: null,
  privateFrom: [],
};

export function useVoiceSnapshot(): VoiceSnapshot {
  return useSyncExternalStore(voiceEngine.subscribe, voiceEngine.getSnapshot, () => EMPTY);
}

/** O mesmo objecto entre chamadas: um literal novo em cada render fazia o
 *  `useSyncExternalStore` julgar que o estado tinha mudado, sem parar. */
const SEM_REDE: Readonly<Record<string, NetSample>> = {};

/**
 * O ping de cada pessoa da call, por identidade.
 *
 * Canal SEPARADO do snapshot de propósito (ver `voice.ts`): o snapshot leva
 * os participantes e os tracks, e o palco inteiro — vídeo incluído — volta a
 * desenhar-se quando ele muda. Quem subscreve isto é só o mostrador da
 * ligação, que se actualiza de cinco em cinco segundos e não arrasta nada.
 */
export function useNetStats(): Readonly<Record<string, NetSample>> {
  return useSyncExternalStore(voiceEngine.subscribeNet, voiceEngine.getNet, () => SEM_REDE);
}

/**
 * Estou mesmo numa chamada AGORA — qualquer que seja o sítio.
 *
 * Reparar que NÃO é `store.voice !== null`: o huddle de corredor liga o motor
 * sem criar sessão na API, e era esse teste que deixava os botões de microfone
 * apagados precisamente na chamada em que se entra sem querer. Quem manda é
 * quem tem o microfone na mão. `reconnecting` conta: a ligação existe, está a
 * ser recuperada, e o botão tem de continuar a poder fechar o microfone.
 */
export function voiceLive(snapshot: VoiceSnapshot): boolean {
  return snapshot.state === "connected" || snapshot.state === "reconnecting";
}

/**
 * O que a barra da chamada diz sobre a ligação — UMA regra, para os dois sítios
 * que a escrevem (o cabeçalho do escritório e a barra do cartão da call).
 *
 * Existe por causa do 2026-09-02: ambos mandavam TUDO o que não fosse
 * "connected"/"failed" para "A ligar…", e `idle` cai nesse tudo. Só que `idle` é
 * o estado do motor PARADO — a sessão na loja continua aberta (o painel está lá)
 * e o motor já desistiu, ou nunca chegou a falar. O ecrã ficava a girar para
 * sempre, e como girar é a cara de "está quase", ninguém ia ver o que se passava.
 *
 * A regra é: só se diz "A ligar…" enquanto ALGUÉM estiver mesmo a tentar. Motor
 * parado com o painel aberto é uma chamada que não aconteceu, e diz-se isso.
 */
export function connectionLabel(state: VoiceConnState): { tone: "connected" | "connecting" | "failed"; text: string } {
  switch (state) {
    case "connected":
      return { tone: "connected", text: "Ligado" };
    case "connecting":
      return { tone: "connecting", text: "A ligar…" };
    case "reconnecting":
      return { tone: "connecting", text: "A recuperar…" };
    default:
      return { tone: "failed", text: "Sem ligação" };
  }
}

/**
 * A regra do botão do microfone — UMA só, para os dois sítios que o desenham (o
 * palco da chamada e a barra da sidebar).
 *
 * Existe porque estava escrita duas vezes e as duas cópias JÁ TINHAM divergido:
 * a barra morria em `reconnecting` (testava `state === "connected"` em vez de
 * `voiceLive`) — exactamente o instante em que mais se quer poder fechar o
 * microfone. Corrigir a linha e deixar a cópia lá seria adiar a divergência
 * seguinte, não fechá-la.
 *
 * E devolve o PORQUÊ, não só o `disabled`: um botão cinzento sem explicação é
 * indistinguível de um botão avariado — foi assim mesmo que este bug chegou
 * («não é possível mutar ou desmutar»). A ordem das razões não é arbitrária:
 * a falta de permissão vem primeiro porque é a que NÃO passa sozinha; a falta
 * de ligação resolve-se em segundos e dizê-la primeiro mandava a pessoa esperar
 * por algo que nunca ia chegar.
 */
export function micControl(
  snapshot: VoiceSnapshot,
  capabilities?: { canSpeak?: boolean } | null,
): { disabled: boolean; title: string } {
  if (capabilities?.canSpeak === false) {
    return { disabled: true, title: "Não tens permissão para falar nesta sala." };
  }
  if (!voiceLive(snapshot)) {
    return { disabled: true, title: "Ainda sem ligação à chamada." };
  }
  return { disabled: false, title: snapshot.micEnabled ? "Silenciar" : "Ativar microfone" };
}

/**
 * O que correu mal, com o motor à frente da API.
 *
 * São duas origens diferentes e só uma estava a ser mostrada: o erro da sessão
 * (`store.voice.error`) é do JOIN — falhou entrar. O erro do motor
 * (`snapshot.error`) é da ACÇÃO — o `setMic` apanhou um `NotAllowedError`, o
 * micro está ocupado por outra aplicação, o token não trazia fonte de áudio.
 * Este segundo nunca chegava a lado nenhum: o clique falhava, o `refresh()`
 * repunha o ícone em «fechado» e o ecrã não dizia uma palavra.
 *
 * O do motor vem primeiro por ser o que a pessoa acabou de provocar — se ambos
 * existem, o de entrar já é história.
 */
export function voiceProblem(snapshot: VoiceSnapshot, sessionError?: string | null): string | null {
  return snapshot.error ?? sessionError ?? null;
}

/** O motor no formato que `ownVoiceMarks` pede, lido fora do React (loop do Phaser). */
export function engineVoice(): EngineVoice {
  const snapshot = voiceEngine.getSnapshot();
  return { connected: voiceLive(snapshot), micEnabled: snapshot.micEnabled, deafened: snapshot.deafened };
}

export interface VoiceActions {
  join(channelId: string): Promise<void>;
  leave(): Promise<void>;
  toggleMic(): Promise<void>;
  toggleDeaf(): Promise<void>;
  toggleCamera(): Promise<void>;
  toggleScreen(): Promise<void>;
  /** Silencia/reactiva o som da MINHA partilha de ecrã (o vídeo continua). Só
   *  tem efeito enquanto partilho ecrã com áudio. */
  toggleScreenAudio(): Promise<void>;
  unlockAudio(): Promise<void>;
  /** Liga/desliga o microfone SEM som nem alternar preferência — o motor do
   *  "premir para falar": segurar a tecla chama `true`, soltar chama `false`. */
  pushTalk(enabled: boolean): Promise<void>;
  raiseHand(up: boolean): void;
  /** Fala só para uma pessoa da call; `null` volta a falar para a sala. */
  setPrivateVoice(identity: string | null): Promise<void>;
  react(emoji: string): void;
  listDevices(kind: MediaDeviceKind): Promise<MediaDeviceInfo[]>;
  setDevice(kind: MediaDeviceKind, deviceId: string): Promise<void>;
  getActiveDevice(kind: MediaDeviceKind): string | undefined;
}

export function useVoiceActions(): VoiceActions {
  const setVoice = useStore((s) => s.setVoice);
  const snapshot = useVoiceSnapshot();

  const join = useCallback(async (channelId: string) => {
    // Estamos dentro de um clique: o momento certo para destrancar o áudio, para
    // que o "join" a seguir à ligação já se ouça (política de autoplay).
    primeSounds();
    const channel = useStore.getState().channels[channelId];
    useStore.setState({
      voice: {
        channelId,
        serverId: channel?.serverId ?? null,
        // Mudo já aqui, antes de a API responder: este estado optimista é o que
        // desenha o meu próprio ícone no primeiro instante da call, e ele tem de
        // dizer a verdade sobre o que o motor vai fazer (`connectMuted`).
        selfMute: true,
        selfDeaf: false,
        screenSharing: false,
        cameraOn: false,
        // Optimista ABERTO, e não fechado como estava. A autoridade real está no
        // token do LiveKit (ver o comentário do `try` a seguir) — isto aqui só
        // desenha botões. Um `false` optimista só é visível quando o join FALHA
        // (durante o join a ligação não está viva e os botões já estão presos por
        // isso), e aí deixava uma chamada à vista com o microfone cinzento e sem
        // explicação, sem outra saída a não ser F5. Aberto, o clique chega ao
        // motor, falha com motivo, e o motivo aparece no ecrã.
        capabilities: { canSpeak: true, canVideo: true, canShareScreen: true, canRecord: true },
        error: null,
      },
    });

    try {
      // A API decide as capacidades a partir das permissões e assina-as DENTRO
      // do token do LiveKit: mesmo que o cliente mentisse, o SFU recusava a
      // publicação. O que vem em `capabilities` é só para desenhar os botões.
      const result = await api.voice.join(channelId);
      useStore.setState((s) => ({
        voice: s.voice ? { ...s.voice, capabilities: result.capabilities } : s.voice,
      }));

      await voiceEngine.connect(connectMuted(result));

      useStore.setState((s) => ({
        voice: s.voice ? { ...s.voice, error: null } : s.voice,
      }));
    } catch (error) {
      useStore.setState((s) => ({
        voice: s.voice ? { ...s.voice, error: (error as Error).message } : s.voice,
      }));
      await api.voice.leave(channelId).catch(() => undefined);
    }
  }, []);

  const leave = useCallback(async () => {
    const current = useStore.getState().voice;
    // Saída à mão: `disconnect()` remove os listeners da sala antes de fechar, por
    // isso o evento `Disconnected` não dispara — o "leave" toca-se aqui.
    playSound("leave");
    await voiceEngine.disconnect();
    useStore.setState({ voice: null });
    if (current) await api.voice.leave(current.channelId).catch(() => undefined);
  }, []);

  const sync = useCallback(
    (patch: { selfMute?: boolean; selfDeaf?: boolean; screenSharing?: boolean; cameraOn?: boolean }) => {
      const current = useStore.getState().voice;
      if (!current) return;
      setVoice(patch);
      void api.voice.updateState(current.channelId, patch).catch(() => undefined);
    },
    [setVoice],
  );

  const toggleMic = useCallback(async () => {
    const enabled = !snapshot.micEnabled;
    playSound(enabled ? "unmute" : "mute");
    await voiceEngine.setMic(enabled);
    sync({ selfMute: !enabled });
  }, [snapshot.micEnabled, sync]);

  const toggleDeaf = useCallback(async () => {
    const deafened = !snapshot.deafened;
    voiceEngine.setDeafened(deafened);
    // Ensurdecer implica silenciar: ouvir-se a falar sem ouvir os outros é o
    // comportamento que toda a gente já espera de um cliente destes.
    if (deafened && snapshot.micEnabled) await voiceEngine.setMic(false);
    sync({ selfDeaf: deafened, ...(deafened ? { selfMute: true } : {}) });
  }, [snapshot.deafened, snapshot.micEnabled, sync]);

  const toggleCamera = useCallback(async () => {
    const enabled = !snapshot.cameraEnabled;
    await voiceEngine.setCamera(enabled);
    sync({ cameraOn: enabled });
  }, [snapshot.cameraEnabled, sync]);

  const toggleScreen = useCallback(async () => {
    const enabled = !snapshot.screenEnabled;
    await voiceEngine.setScreenShare(enabled);
    // O snapshot é a verdade: se o utilizador cancelou o diálogo do browser,
    // `screenEnabled` continua falso e não podemos anunciar o contrário.
    sync({ screenSharing: voiceEngine.getSnapshot().screenEnabled });
  }, [snapshot.screenEnabled, sync]);

  // Não sincroniza com a API: o silêncio é estado de media puro — os outros
  // clientes deixam de ouvir porque o track fica mudo no SFU, sem passar pelo
  // nosso servidor. `screenSharing` permanece verdadeiro (continuo a mostrar).
  const toggleScreenAudio = useCallback(
    () => voiceEngine.setScreenAudioMuted(!snapshot.screenAudioMuted),
    [snapshot.screenAudioMuted],
  );

  const unlockAudio = useCallback(() => voiceEngine.unlockAudio(), []);

  const pushTalk = useCallback(
    async (enabled: boolean) => {
      // Premir para falar não pode dar voz a quem não tem direito a ela: mesma
      // barreira que o botão do micro (a autoridade final é o token do SFU).
      if (useStore.getState().voice?.capabilities.canSpeak === false) return;
      await voiceEngine.setMic(enabled);
      sync({ selfMute: !enabled });
    },
    [sync],
  );

  const raiseHand = useCallback((up: boolean) => voiceEngine.raiseHand(up), []);
  const setPrivateVoice = useCallback((identity: string | null) => voiceEngine.setPrivateVoice(identity), []);
  const react = useCallback((emoji: string) => voiceEngine.sendReaction(emoji), []);
  const listDevices = useCallback((kind: MediaDeviceKind) => voiceEngine.listDevices(kind), []);
  const setDevice = useCallback((kind: MediaDeviceKind, deviceId: string) => voiceEngine.setDevice(kind, deviceId), []);
  const getActiveDevice = useCallback((kind: MediaDeviceKind) => voiceEngine.getActiveDevice(kind), []);

  return {
    join,
    leave,
    toggleMic,
    toggleDeaf,
    toggleCamera,
    toggleScreen,
    toggleScreenAudio,
    unlockAudio,
    pushTalk,
    raiseHand,
    setPrivateVoice,
    react,
    listDevices,
    setDevice,
    getActiveDevice,
  };
}
